import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Request from '../../services/request';
import Notifications from '../../services/notifications';
import FormUserTypeInput from '../../commonComponents/forms/FormUserTypeInput';
import FormInput from '../../commonComponents/forms/FormInput';
import Form from '../../commonComponents/forms/Form';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import TenantProp from '../../commonComponents/propTypes/tenantProp';
import CurrencyProp from '../profile/propTypes/currencyProp';
import { compose } from '../../services/utilityFunctions';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import TermsAndPolicyCheckbox from '../../commonComponents/forms/TermsAndPolicyCheckbox';

function SignUpForm(props) {
  const {
    t,
    _csrf,
    onComplete,
    languages,
    organizationIdentifier,
    tenant,
    buttonTxt,
    currencies,
    countries,
  } = props;

  const [checkbox, setCheckbox] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    language: 'en',
    shippingCountry: '',
    currency: '',
    type: 'customer',
  });

  const {
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    language,
    currency,
    shippingCountry,
    type,
  } = form;

  const handleForm = useCallback(
    field => value => setForm(prevForm => ({ ...prevForm, [field]: value })),
    [setForm],
  );

  const handleCheckbox = () => {
    setCheckbox(!checkbox);
  };

  const validationRules = {
    firstName: { required: true },
    lastName: { required: true },
    email: { required: true, email: true },
    password: { required: true, minlength: 6 },
    language: { required: true },
    confirm_password: { required: true, equalTo: '#password' },
  };

  const submitButtonClasses = cx(
    'btn',
    'btn-dynamic',
    'm-btn',
    'm-btn--pill',
    'm-btn--custom',
    'm-btn--air',
    'm-login__btn',
    { 'm-loader': isLoading },
    { 'm-loader--right': isLoading },
    { 'm-loader--light': isLoading },
  );

  const onSignUp = () => {
    setIsLoading(true);
    const params = {
      firstName,
      lastName,
      username: `${firstName} ${lastName}`,
      email,
      password,
      language,
      organizationIdentifier,
      shippingCountry,
      type,
      currency,
      omitLogin: false,
    };

    new Request(_csrf)
      .post('/signin/signUp', params)
      .done(onComplete)
      .fail(err => {
        setIsLoading(false);
        Notifications.showErrorFromRequest(err);
      });
  };

  const usersCanChooseTheirType = tenant.settings?.usersCanChooseTheirType;
  return (
    <div className="m-content m-portlet session-public-enroll py-5">
      <div className="session-public-enroll-form">
        <Form
          className="m-form m-form--fit m-form--label-align-right"
          id="sessionSubscribeForm"
          submitMethod={onSignUp}
          validationRules={validationRules}
        >
          {submitHandler => (
            <>
              {
                <>
                  {usersCanChooseTheirType && (
                    <FormUserTypeInput
                      labelClassName="col-lg-4 col-sm-12"
                      containerClassName="col-lg-8 col-sm-12"
                      onChange={handleForm('type')}
                      value={type}
                    />
                  )}
                  <FormInput
                    id="firstName"
                    name="firstName"
                    type="text"
                    label={t('profile_firstName')}
                    labelClassName="col-lg-4 col-sm-12"
                    inputProps={{ autoComplete: 'off' }}
                    placeholder={t('profile_firstNamePlaceholder')}
                    value={firstName}
                    onChange={handleForm('firstName')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-8 col-sm-12"
                  />
                  <FormInput
                    id="lastName"
                    name="lastName"
                    type="text"
                    label={t('profile_lastName')}
                    labelClassName="col-lg-4 col-sm-12"
                    inputProps={{ autoComplete: 'off' }}
                    placeholder={t('profile_lastNamePlaceholder')}
                    value={lastName}
                    onChange={handleForm('lastName')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-8 col-sm-12"
                  />
                  <FormInput
                    id="email"
                    type="email"
                    name="email"
                    label={t('profile_email')}
                    labelClassName="col-lg-4 col-sm-12"
                    inputProps={{ autoComplete: 'off' }}
                    placeholder={t('email_holder')}
                    value={email}
                    onChange={handleForm('email')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-8 col-sm-12"
                  />
                  <FormInput
                    id="password"
                    name="password"
                    type="password"
                    label={t('profile_password')}
                    labelClassName="col-lg-4 col-sm-12"
                    placeholder={t('pass_holder')}
                    value={password}
                    onChange={handleForm('password')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-8 col-sm-12"
                  />
                  <FormInput
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    label={t('profile_confirmPassword')}
                    labelClassName="col-lg-4 col-sm-12"
                    placeholder={t('reTypePass_holder')}
                    value={confirmPassword}
                    onChange={handleForm('confirmPassword')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-8 col-sm-12"
                  />
                  <FormInput
                    id="country"
                    name="country"
                    type="select"
                    label={t('profile_shippingCountry')}
                    labelClassName="col-lg-4 col-sm-12"
                    placeholder={t('profile_shippingCountry')}
                    value={shippingCountry}
                    onChange={handleForm('shippingCountry')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-8 col-sm-12"
                    inputProps={{
                      items: countries,
                    }}
                  />
                  <FormInput
                    id="currency"
                    name="currency"
                    type="select"
                    label={t('profile_currency')}
                    labelClassName="col-lg-4 col-sm-12"
                    placeholder={t('profile_currency')}
                    value={currency}
                    onChange={handleForm('currency')}
                    groupProps={{ className: 'row' }}
                    containerClassName="col-lg-8 col-sm-12"
                    inputProps={{
                      items: currencies,
                    }}
                  />
                  <FormInput
                    id="language"
                    name="language"
                    type="select"
                    value={language}
                    inputProps={{
                      items: languages.map(l => ({ id: l.code, text: l.name })),
                    }}
                    onChange={value => {
                      handleForm('language')(value);
                    }}
                    label={t('profile_language')}
                    groupProps={{ className: 'row' }}
                    labelClassName="col-lg-4 col-sm-12"
                    containerClassName="col-lg-8 col-sm-12"
                  />
                  <div className="checkbox-terms-and-policy-wrapper">
                    <TermsAndPolicyCheckbox
                      checkboxValue={checkbox}
                      handleCheckbox={handleCheckbox}
                      tenant={tenant}
                    />
                  </div>
                  <div className="login-section">
                    <button
                      id="createAccount"
                      type="submit"
                      className={submitButtonClasses}
                      disabled={!checkbox || isLoading}
                      onClick={submitHandler}
                    >
                      {buttonTxt}
                    </button>
                  </div>
                </>
              }
            </>
          )}
        </Form>
      </div>
    </div>
  );
}

SignUpForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  onComplete: PropTypes.func,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  organizationIdentifier: PropTypes.string.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  buttonTxt: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
};

export default compose(translate, withCSRF)(SignUpForm);
